import axios from "axios";

// Keep the API same-site as the page so the login cookie is first-party on each domain.
const backendUrl = () => {
  const h = typeof window !== "undefined" ? window.location.hostname : "";
  if (h.endsWith("kholidarchery.web.id")) return "https://api.kholidarchery.web.id";
  return process.env.REACT_APP_BACKEND_URL;
};
export const api = axios.create({ baseURL: `${backendUrl()}/api`, withCredentials: true });
// 503 = server locked / maintenance: let the app shell switch to the neutral maintenance view.
api.interceptors.response.use((r) => r, (e) => { if (e?.response?.status === 503 && typeof window !== "undefined") window.dispatchEvent(new Event("kha-maintenance")); return Promise.reject(e); });
export const levels = ["Mubtadi", "Muhtadi", "Amir", "Pengabdian"];
export const levelTags = ["Pemula", "Berkembang", "Terarah", "Teladan"];
export const levelCode = (l) => String(Number(l) || 1).padStart(2, "0");
export const levelLabel = (l) => `${levelCode(l)} ${levels[l - 1] || ""}`;
export const CATEGORIES = ["Pemula", "Dasar", "Menengah", "Lanjutan", "Elit"];
export const catLabel = (a) => a?.category || "Kategori perlu diatur";
export const canManage = (user) => user.role === "admin" || user.role === "coach";
export const rupiah = (value) => new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", maximumFractionDigits: 0 }).format(value || 0);
export const periodLabel = (period) => { const [y, m] = period.split("-"); return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString("id-ID", { month: "long", year: "numeric" }); };
export const currentPeriod = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; };
export const errorText = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d) && d.length) return `Data tidak valid: ${d.map((x) => `${x.loc?.slice(-1)[0] ?? ""} ${x.msg}`.trim()).join("; ")}`;
  return "Terjadi kendala. Silakan coba lagi.";
};
export const DISCIPLINES = ["Recurve", "Barebow", "Compound", "Traditional", "Standard", "Horsebow"];

/** Resize to max 1600px and re-encode (WebP, JPEG fallback) until under ~300 KB, then upload (admin only). */
export async function compressImage(file, { max = 1600, target = 300 * 1024 } = {}) {
  if (!/^image\/(jpeg|png|webp|gif)$/.test(file.type)) throw new Error("Format gambar harus JPG, PNG, WebP, atau GIF");
  if (file.size > 25 * 1024 * 1024) throw new Error("File terlalu besar (maks. 25 MB sebelum kompresi)");
  const bmp = await createImageBitmap(file).catch(() => { throw new Error("Gambar tidak dapat dibaca"); });
  let scale = Math.min(1, max / Math.max(bmp.width, bmp.height));
  const canvas = document.createElement("canvas");
  const probe = canvas.toDataURL("image/webp").startsWith("data:image/webp") ? "image/webp" : "image/jpeg";
  for (let attempt = 0; attempt < 8; attempt++) {
    canvas.width = Math.max(1, Math.round(bmp.width * scale)); canvas.height = Math.max(1, Math.round(bmp.height * scale));
    const ctx = canvas.getContext("2d"); ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, canvas.width, canvas.height); ctx.drawImage(bmp, 0, 0, canvas.width, canvas.height);
    for (const q of [0.82, 0.72, 0.62, 0.5]) {
      const blob = await new Promise((res) => canvas.toBlob(res, probe, q));
      if (blob && blob.size <= target) return blob;
    }
    scale *= 0.8;
  }
  throw new Error("Gambar tidak dapat dikompresi di bawah 300 KB");
}
export async function uploadImage(file) {
  const blob = await compressImage(file);
  const { data } = await api.post("/images", blob, { headers: { "Content-Type": blob.type } });
  return data.url;
}

/** Today as YYYY-MM-DD in the device's local time (WIB), not UTC — toISOString() gives yesterday before 07:00 WIB. */
export const localToday = (d = new Date()) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
/** Check-in time shown in WIB regardless of the device zone: "18.03" / "Sen, 18 Okt 2026 18.03 WIB". */
export const wibTime = (iso) => (iso ? new Date(iso).toLocaleTimeString("id-ID", { timeZone: "Asia/Jakarta", hour: "2-digit", minute: "2-digit" }) : "");
export const wibDateTime = (iso) => (iso ? `${new Date(iso).toLocaleDateString("id-ID", { timeZone: "Asia/Jakarta", weekday: "short", day: "numeric", month: "short", year: "numeric" })} ${wibTime(iso)} WIB` : "");
export const attTime = (rec) => rec?.checked_in_at || rec?.recorded_at || rec?.updated_at || null;
